import * as React from 'react';
import { cn } from '@/lib/utils';

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: 'default' | 'outline' | 'secondary' | 'pill';
  /** Fully-rounded capsule rather than the default soft rectangle. */
  round?: boolean;
}

// 36 hand-rolled chips existed across the app alongside only 30 uses of this
// component, and the reason was in the primitive: `rounded-md` was hard-coded
// with no way to get a capsule, so anyone who wanted `rounded-full` had to
// write the whole chip by hand. `round` closes that gap.
export function Badge({ className, variant = 'default', round, ...props }: BadgeProps) {
  const base = cn(
    // `shrink-0` + `whitespace-nowrap` are load-bearing, not cosmetic. Without
    // them a badge inside a tight flex row (the ticket list) gets compressed
    // and its label is clipped mid-word — "Waiting on client" rendered as
    // "Waiting on clien". A status that can't be read is worse than no status.
    'inline-flex shrink-0 items-center whitespace-nowrap px-1.5 py-0.5 text-2xs font-medium leading-4 transition-colors',
    round ? 'rounded-full' : 'rounded-md',
  );
  const variants: Record<NonNullable<BadgeProps['variant']>, string> = {
    default: 'border border-transparent bg-primary text-primary-foreground',
    outline: 'border border-border text-foreground',
    secondary: 'border border-transparent bg-secondary text-secondary-foreground',
    // pill: caller supplies the colour classes (e.g. priorityColor / statusColor).
    pill: '',
  };
  return <span className={cn(base, variants[variant], className)} {...props} />;
}
